import React, { useMemo, useState } from 'react';
import OtpVerification from '../otp-verification/otp-verification.component';
import { UserPersonalInfo } from '../../../stores/types/user.types';
import { useUserProfile } from '../../../hooks/useUserProfile';
import { UserMode } from '../types/user-mode.enum';
import { PhoneNumberAlreadyExistsError } from '../../../errors/phone-number-already-exists';
import { BackButtonHeader } from '../../ui-components/back-button-header.component';
import { PhoneNumberField } from '../../ui-components/phone-number-field.component';
import { FormLabel, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { emailStyles } from '../edit-email/styles';
import ActionButton from '../action-button.component';
import clsx from 'clsx';
import { showSnackBar } from '../../../util/toast.utility';
import { ColorType } from '../../../assets/colors/colors.g';
import { useLogger } from '../../../services/logging/logger-context';

interface IProps {
  currentUser: UserPersonalInfo;
  handleUserView: Function;
}

const EditPhone = ({ currentUser, handleUserView }: IProps) => {
  const { t } = useTranslation();
  const { getPhoneNumber, updateUserPhone, verifyPhoneCode } = useUserProfile();
  const { logger } = useLogger();

  const existingPhoneNormalized = getPhoneNumber();
  const [verifyOtp, setVerifyOtp] = useState(false);
  const [phoneNormalized, setPhoneNormalized] = useState('');
  const [phone, setPhone] = useState(existingPhoneNormalized);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [otpError, setOtpError] = useState<boolean>(false);
  const [phoneNumberInUseError, setPhoneNumberInUseError] = useState<boolean>(false);
  const classes = emailStyles();

  // Check if the current phone is the same as the new phone entered by the user
  const phoneNumberChanged = useMemo(
    () => existingPhoneNormalized !== phoneNormalized,
    [existingPhoneNormalized, phoneNormalized],
  );

  const verifyUserPhone = async () => {
    try {
      await updateUserPhone(phoneNormalized);
      setVerifyOtp(true);
      setOtpError(false);
    } catch (e) {
      logger.error('Failed to update user phone', e);
      showSnackBar(t('unknown_error'));
    }
  };

  const verifyPhoneValidationCode = async (code) => {
    try {
      await verifyPhoneCode(code);
      handleUserView(UserMode.NONE);
    } catch (e) {
      if (e instanceof PhoneNumberAlreadyExistsError) {
        setOtpError(false);
        setVerifyOtp(false);
        setPhoneNumberInUseError(true);
        logger.error('Failed to update phone number because it is already in use', e);
      } else {
        setOtpError(true);
      }
    }
  };

  const onClickBack = () => {
    handleUserView(UserMode.NONE);
  };

  return (
    <>
      {verifyOtp ? (
        <>
          <BackButtonHeader title={t('verify_phone_number')} onBackArrowClicked={onClickBack} />
          <OtpVerification
            otpLength={6}
            error={otpError}
            setError={setOtpError}
            verifyUser={phoneNormalized}
            handleVerifyOtp={verifyPhoneValidationCode}
            sendNewCode={verifyUserPhone}
            description={'we_have_sent_code'}
            description1={t('enter_code_to_verify_phone')}
          />
        </>
      ) : (
        <>
          <BackButtonHeader title={t('phone_number')} onBackArrowClicked={onClickBack} />
          <Grid className={classes.formWrapper}>
            <FormLabel htmlFor='phone_number' className={classes.title}>
              {t('phone_number')}
            </FormLabel>
            <Grid
              container
              className={clsx(classes.displayRelative, classes.marginTop8)}
              sx={{ gap: 1 }}
            >
              <PhoneNumberField
                value={phone}
                onChange={(newPhone) => {
                  setPhone(newPhone);
                  setPhoneNumberInUseError(false);
                }}
                required={true}
                onValidationChange={(isValid, normalized) => {
                  setIsPhoneValid(isValid);
                  setPhoneNormalized(normalized);
                }}
                inputProps={{
                  'aria-label': t('canada_selected'),
                }}
              />
              {phoneNumberInUseError && (
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: ColorType.NEGATIVE,
                  }}
                >
                  {t('phone_number_is_taken')}
                </Typography>
              )}
            </Grid>
          </Grid>
          <ActionButton
            label={t('next')}
            action={verifyUserPhone}
            isDisabled={!phoneNumberChanged || !isPhoneValid}
          />
        </>
      )}
    </>
  );
};

export default EditPhone;
